import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { HttpService } from 'src/app/services/http/http.service';

interface AlertItem {
  show: boolean;
  message: string;
  routeKey: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit {
  activePeriod: 'monthly' | 'yearly' = 'monthly';
  dashboardRawData: any;

  constructor(
    private router: Router,
    private http: HttpService
  ) { }

  ngOnInit(): void {
    this.loadDashboard();
  }

  /* ================= KPI ================= */
  businessKpi = [
    {
      label: 'CRF',
      icon: 'assignment',
      bgColor: '#E3F2FD',
      hasPeriod: true,
      monthly: 0,
      yearly: 0
    },
    {
      label: 'Samples',
      icon: 'layers',
      bgColor: '#E8F5E9',
      hasPeriod: true,
      monthly: 0,
      yearly: 0
    },
    {
      label: 'Reports',
      icon: 'description',
      bgColor: '#FFF8E1',
      hasPeriod: true,
      monthly: 0,
      yearly: 0
    },
    {
      label: 'Pending Tasks',
      icon: 'schedule',
      bgColor: '#FCE4EC',
      value: 0
    }
  ];

  /* ================= PIPELINE ================= */
  pipeline = [
    {
      title: 'CRF Status',
      periodAware: true,
      statuses: [
        { type: 'approved', label: 'Approved', value: 0, icon: '✔' },
        { type: 'pending', label: 'Pending', value: 0, icon: '⚠' }
      ]
    },
    {
      title: 'Sample Status',
      periodAware: true,
      statuses: [
        { type: 'success', label: 'Success', value: 0, icon: '✔' },
        { type: 'pending', label: 'Pending', value: 0, icon: '⚠' },
        { type: 'rejected', label: 'Rejected', value: 0, icon: '✖' },
        { type: 'info', label: 'Allocated', value: 0, icon: '📦' }
      ]
    },
    {
      title: 'Test Report',
      periodAware: true,
      statuses: [
        { type: 'success', label: 'Passed', value: 0, icon: '✔' },
        { type: 'pending', label: 'Pending', value: 0, icon: '⚠' },
        { type: 'rejected', label: 'Rejected', value: 0, icon: '✖' }
      ]
    }
  ];

  alerts: AlertItem[] = [];


  /* ================= API ================= */
  loadDashboard(): void {
    this.http.getMethod('login/getDashboardSummary').subscribe((res: any) => {
      if (!res?.result) return;
      this.dashboardRawData = res.result;

      const r = res.result;

      // CRF KPI
      this.setKpiToggleValue(
        'CRF',
        r.crf.monthly.pending + r.crf.monthly.approved,
        r.crf.yearly.pending + r.crf.yearly.approved
      );

      // REPORT KPI
      this.setKpiToggleValue(
        'Reports',
        r.report.monthly.pending + r.report.monthly.success + r.report.monthly.rejected,
        r.report.yearly.pending + r.report.yearly.success + r.report.yearly.rejected
      );

      // SAMPLE KPI (MONTHLY / YEARLY)
      this.setKpiToggleValue(
        'Samples',
        r.sample.monthly.pending +
        r.sample.monthly.success +
        r.sample.monthly.rejected ,
        

        r.sample.yearly.pending +
        r.sample.yearly.success +
        r.sample.yearly.rejected 
       
      );


      this.setKpiValue('Pending Tasks', r.report.overall.pending);

      this.applyAllPipelines();
      this.buildAlerts();
    });
  }

  /* ================= PERIOD ================= */
  changePeriod(period: 'monthly' | 'yearly'): void {
    this.activePeriod = period;
    this.applyAllPipelines();
  }
  get currentPeriodLabel(): string {
  const now = new Date();

  if (this.activePeriod === 'monthly') {
    return now.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
  }

  return now.getFullYear().toString();
}

  applyAllPipelines(): void {
    if (!this.dashboardRawData) return;
    const r = this.dashboardRawData;
    const p = this.activePeriod;

    // CRF
    this.setPipelineValue('CRF Status', 'Approved', r.crf[p].approved);
    this.setPipelineValue('CRF Status', 'Pending', r.crf[p].pending);

    // SAMPLE
    this.setPipelineValue('Sample Status', 'Success', r.sample[p].success);
    this.setPipelineValue('Sample Status', 'Pending', r.sample[p].pending);
    this.setPipelineValue('Sample Status', 'Rejected', r.sample[p].rejected);
    this.setPipelineValue('Sample Status', 'Allocated', r.sample.allocated[p]);

    // REPORT
    this.setPipelineValue('Test Report', 'Passed', r.report[p].success);
    this.setPipelineValue('Test Report', 'Pending', r.report[p].pending);
    this.setPipelineValue('Test Report', 'Rejected', r.report[p].rejected);
  }

  /* ================= HELPERS ================= */
  setKpiToggleValue(label: string, monthly: number, yearly: number): void {
    const kpi = this.businessKpi.find(k => k.label === label);
    if (kpi) {
      kpi.monthly = monthly;
      kpi.yearly = yearly;
    }
  }

  setKpiValue(label: string, value: number): void {
    const kpi = this.businessKpi.find(k => k.label === label);
    if (kpi) kpi.value = value;
  }

  setPipelineValue(section: string, label: string, value: number): void {
    const card = this.pipeline.find(p => p.title === section);
    const status = card?.statuses.find(s => s.label === label);
    if (status) status.value = value;
  }

  buildAlerts(): void {
    this.alerts = [
      {
        show: this.getValue('Sample Status', 'Pending') > 0,
        message: `High Sample Pending: ${this.getValue('Sample Status', 'Pending')}`,
        routeKey: 'High Sample Pending'
      }
    ];
  }

  getValue(card: string, label: string): number {
    return this.pipeline
      .find(p => p.title === card)
      ?.statuses.find(s => s.label === label)?.value ?? 0;
  }

  onStatusClick(e: MouseEvent, section: string, label: string, value: number) {
    e.stopPropagation();
    if (value <= 0) return;
    this.onCardClick(section, label);
  }


  onCardClick(section: string, label?: string) {
    const key = label ? `${section}|${label}` : section;

    const routeMap: Record<string, any[]> = {
      // KPI
      'Total CRF': ['/crf'],
      'Samples': ['/samples'],
      'Reports': ['/reports'],
      'Pending Tasks': ['/tasks', 'pending'],

      // CRF
      'CRF Status|Approved': ['/CRF', 'CRF', 'manage-crf'],
      'CRF Status|Pending': ['/CRF', 'CRF', 'manage-crf'],
      'CRF Status|Rejected': ['/CRF', 'CRF', 'manage-crf'],

      // Samples
      'Sample Status|Success': ['/sample-verification', 'verification', 'sample-list'],
      'Sample Status|Pending': ['/sample-verification', 'verification', 'sample-list'],
      'Sample Status|Rejected': ['/sample-verification', 'verification', 'sample-list'],
      'Sample Status|Allocated': ['/sample-verification', 'verification', 'sample-list'],

      // Reports
      'Test Report|Passed': ['/report', 'Test-Result'],
      'Test Report|Pending': ['/report', 'Test-Result'],
      'Test Report|Rejected': ['/report', 'Test-Result'],

      // Alerts
      'Alert|High Sample Pending': ['/sample-verification', 'verification', 'sample-list'],
      'Alert|High Report Pending': ['/report', 'Test-Result']
    };

    const route = routeMap[key];

    if (route) {
      this.router.navigate(route);
    } else {
      console.warn('No route defined for:', key);
    }
  }
}
